import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  detailsOpen: boolean
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      detailsOpen: false,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, info)
  }

  handleToggleDetails = (): void => {
    this.setState((prev) => ({ detailsOpen: !prev.detailsOpen }))
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    const isDev = process.env.NODE_ENV === 'development'
    const { error, detailsOpen } = this.state

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a2233' : '#f4f6f8',
          fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
          p: 2,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            borderRadius: '24px',
            maxWidth: 520,
            width: '100%',
            p: 5,
            textAlign: 'center',
            fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00cff3 0%, #33ebcb 100%)',
              mx: 'auto',
              mb: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
              fontWeight: 700,
              color: (theme) => theme.palette.text.primary,
              mb: 1.5,
            }}
          >
            Something went wrong
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
              color: (theme) => theme.palette.text.secondary,
              mb: 4,
            }}
          >
            An unexpected error occurred. Please try refreshing the page.
          </Typography>

          <Button
            onClick={() => window.location.reload()}
            sx={{
              background: 'linear-gradient(135deg, #00cff3 0%, #33ebcb 100%)',
              color: '#fff',
              borderRadius: '40px',
              textTransform: 'uppercase',
              fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
              fontWeight: 700,
              px: 4,
              py: 1.25,
              fontSize: '0.875rem',
              letterSpacing: '0.08em',
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #00b8d9 0%, #1dd6b5 100%)',
                boxShadow: '0 4px 16px rgba(0, 207, 243, 0.35)',
              },
            }}
          >
            Refresh Page
          </Button>

          {isDev && error && (
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Button
                onClick={this.handleToggleDetails}
                size="small"
                sx={{
                  color: '#007d8a',
                  textTransform: 'none',
                  fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
                  fontSize: '0.8rem',
                  p: 0,
                  minWidth: 0,
                  '&:hover': { background: 'transparent', textDecoration: 'underline' },
                }}
              >
                {detailsOpen ? 'Hide' : 'Show'} error details
              </Button>
              <Collapse in={detailsOpen}>
                <Box
                  component="pre"
                  sx={{
                    mt: 1.5,
                    p: 2,
                    borderRadius: '8px',
                    backgroundColor: '#1a2233',
                    color: '#ff6b6b',
                    fontSize: '0.75rem',
                    fontFamily: '"Courier New", Courier, monospace',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  <strong>{error.message}</strong>
                  {error.stack ? `\n\n${error.stack}` : ''}
                </Box>
              </Collapse>
            </Box>
          )}
        </Paper>
      </Box>
    )
  }
}

export default ErrorBoundary
